import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
  redirect,
} from '@remix-run/node';
import {
  Link,
  Outlet,
  type ShouldRevalidateFunction,
  useLoaderData,
  useParams,
} from '@remix-run/react';
import { startCase } from 'lodash-es';
import React, { lazy, type ReactNode, Suspense } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { ThemeSwitch } from '#app/components/theme-switch.tsx';
import { Button, buttonVariants } from '#app/components/ui/button.tsx';
import { DatePickerRange } from '#app/components/ui/date-picker-range.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '#app/components/ui/select.tsx';
import { AlertsTableModel } from '#app/models/alerts-table/index.ts';
import { getHeatmapModel } from '#app/models/heatmap/index.ts';
import { SyncLineChartModel } from '#app/models/sync-line-chart/index.ts';
import { sanitizeOptions } from '#app/routes/widgets+/__widget-utils.ts';
import { Heatmap } from '#app/routes/widgets+/heatmap.tsx';
import { SynchronizedLineChart } from '#app/routes/widgets+/synchronized-line-chart.tsx';
import {
  type AlertsTableChart,
  type Layer,
  type Chart,
  type Dashboard,
  type HeatmapInputData,
  type AlertsTableInputData,
  type SyncLineChartData,
  type SyncLineChartSchema,
  type HeatmapSchema,
  type AlertsLayer,
} from '#app/types';
import { getDefaultEndDate, getDefaultStartDate } from '#app/utils/dates.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { getWidgetData } from '#app/utils/get-widget-data.ts';
import { cn, getDomainUrl } from '#app/utils/misc.tsx';
import { useDashboardSettings } from '#app/utils/use-dashboard-settings.ts';
import { useOptimisticTheme } from '#app/utils/use-optimistic-theme.ts';

const MapClient = lazy(
  () => import('../../components/dashboard-map/deckgl-map.tsx'),
);

export const meta: MetaFunction = ({ params }) => {
  const slug = startCase(params?.slug);

  return [{ title: `${slug} | Xouba Dashboard` }];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url);
  const options = sanitizeOptions(searchParams, {
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(searchParams),
  });
  const heatmapModel = getHeatmapModel(options.startDate, options.endDate);
  const syncLineChartModel = new SyncLineChartModel(
    options.startDate,
    options.endDate,
  );
  const alertsTableModel = new AlertsTableModel(
    options.startDate,
    options.endDate,
  );

  const origin = getDomainUrl(request);

  const dashboardsList = await prisma.dashboard.findMany({
    select: {
      slug: true,
      name: true,
      app: true,
    },
    where: {
      hidden: false,
      app: 'tunnels',
    },
  });

  if (params?.slug == null) {
    const defaultDashboard = dashboardsList[0]?.slug;
    if (defaultDashboard == null) {
      return json(
        {
          options,
          dashboard: null,
        },
        { status: 404 },
      );
    }
    return redirect(`/tunnels/${defaultDashboard}`);
  }

  const dashboardResult = await prisma.dashboard.findFirst({
    where: {
      slug: params.slug,
    },
  });

  if (dashboardResult == null || dashboardResult.meta == null) {
    return json(
      {
        options,
        dashboard: null,
      },
      { status: 404 },
    );
  }

  const dashboard: Dashboard = {
    ...dashboardResult,
    meta: JSON.parse(dashboardResult.meta) as Dashboard['meta'],
  };

  const dashboardLayers = await prisma.layer.findMany({
    where: {
      dashboardId: dashboard.id,
      hidden: false,
    },
  });

  const dashboardCharts = await prisma.chart.findMany({
    where: {
      dashboardId: dashboard.id,
      hidden: false,
    },
    orderBy: {
      position: 'asc',
    },
  });

  const layers: Layer[] = await Promise.all(
    dashboardLayers.map(async (layer) => {
      const data = await getWidgetData<Layer['data']>(layer, origin, {
        startDate: options.startDate,
        endDate: options.endDate,
      });
      let meta = null;
      try {
        meta =
          layer.meta != null && layer.meta !== ''
            ? (JSON.parse(layer.meta) as Layer['meta'])
            : null;
      } catch (error) {
        console.log(layer);
        console.error('Error parsing layer meta', error);
      }

      return {
        ...layer,
        data,
        meta,
      };
    }),
  );

  const chartsData: Chart['data'][] = await Promise.all(
    dashboardCharts.map((chart) =>
      getWidgetData<Chart['data']>(chart, origin, {
        startDate: options.startDate,
        endDate: options.endDate,
      }),
    ),
  );

  const charts: Chart[] = dashboardCharts.map((chart, index: number) => {
    const rawData = chartsData[index];

    if (chart.type === 'heatmap') {
      return heatmapModel.buildChart(chart, rawData as HeatmapInputData);
    }

    if (chart.type === 'alerts-table') {
      const tableChart = alertsTableModel.buildChart(
        chart,
        rawData as AlertsTableInputData,
      );

      tableChart.data.rows.forEach((row) => {
        if (row.geojson == null) {
          return;
        }
        const alertsLayer: AlertsLayer = {
          id: `alerts-${chart.id}-${row.id}`,
          meta: null,
          type: 'alerts',
          dataUrl: false,
          data: row.geojson,
          createdAt: new Date(),
          updatedAt: new Date(),
          dashboardId: chart.dashboardId,
        };
        layers.push(alertsLayer);
      });

      // Since we're not gonna use the real data and it can be expensive to send to the client
      // we're gonna remove it from the response
      tableChart.data.rows = [];

      return tableChart;
    }

    if (chart.type === 'synchronized-line-chart') {
      const tableIndex = dashboardCharts.findIndex(
        (c) => c.type === 'alerts-table',
      );
      const tableData = chartsData[tableIndex] as
        | AlertsTableInputData
        | undefined;

      return syncLineChartModel.buildChart(
        chart,
        rawData as SyncLineChartData,
        tableData,
      );
    }

    throw new Error(`Unknown chart type: ${chart.type}`);
  });

  return json({
    options,
    dashboard: {
      layers,
      charts,
      dashboardsList,
      mapViewState: dashboard.meta.mapViewState,
      title: dashboard.name,
      showExport: dashboard.meta.showExport,
      showDatePicker: dashboard.meta.showDatePicker,
    },
  });
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
}) => {
  return currentUrl.toString() != nextUrl.toString();
};

export default function TunnelsLayout() {
  const params = useParams();
  const { options, dashboard } = useLoaderData<typeof loader>();
  const { theme, requestInfo } = useOptimisticTheme();
  const { onSetDateRange, onSlugChange, onSelectExport, isDev } =
    useDashboardSettings(params.slug as string, options ?? {});

  if (dashboard == null) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <h1 className="text-4xl text-accent-foreground">
          Error: No se encontró el dashboard
        </h1>
      </div>
    );
  }

  const {
    layers,
    charts,
    dashboardsList,
    showExport,
    showDatePicker,
    mapViewState,
  } = dashboard;

  const table = charts.find((chart) => chart.type === 'alerts-table') as
    | AlertsTableChart
    | undefined;
  const hasTable = table != null && table.data?.summary[0] != null;

  const selectedTunnel = dashboardsList.find(
    (tunnel) => tunnel.slug === params.slug,
  );

  let selectedTunnelValue: ReactNode = (
    <span className="text-accent-foreground">Selecciona un túnel</span>
  );
  if (selectedTunnel != null) {
    const results = selectedTunnel.name.match(/^(túnel (del?\s)?)(.*)$/i);
    if (!results || results[1] == null || results[3] == null) {
      selectedTunnelValue = (
        <span className="text-accent-foreground">{selectedTunnel.name}</span>
      );
    } else {
      selectedTunnelValue = (
        <>
          <span className="mr-2 inline-block text-accent-foreground">
            {results[1]}
          </span>
          <wbr />
          <mark className="whitespace-nowrap bg-transparent text-primary">
            {results[3]}
          </mark>
        </>
      );
    }
  }

  return (
    <main className="pointer-events-auto grid max-h-full w-full">
      <div className="flex h-full w-full overflow-hidden">
        <div
          className={cn('flex h-full w-2/3 flex-col bg-background pt-4', {
            relative: isDev,
          })}
        >
          {isDev && (
            <div className="absolute left-0 top-0">
              <ThemeSwitch userPreference={requestInfo.userPrefs.theme} />
            </div>
          )}
          <div className="flex items-center justify-end">
            <div className="grid w-full grid-cols-12 gap-x-5 px-5 pb-4">
              <div
                className={cn(
                  'flex items-center justify-between rounded-2xl bg-secondary p-6',
                  {
                    'col-span-12': !hasTable,
                    'col-span-9': hasTable,
                  },
                )}
              >
                <div className="flex w-full flex-col items-start justify-start gap-y-4">
                  <div className="flex w-full items-center justify-between gap-x-2">
                    <img
                      src={`/img/xouba-logo-${theme}.png`}
                      alt="Xouba"
                      className="h-8"
                    />
                    {showDatePicker && (
                      <DatePickerRange
                        className="w-auto p-2"
                        fromDate={new Date(options.startDate)}
                        toDate={new Date(options.endDate)}
                        onSelect={onSetDateRange}
                        initialFocus={false}
                      />
                    )}
                  </div>
                  <Select value={params.slug} onValueChange={onSlugChange}>
                    <SelectTrigger
                      className={cn(
                        buttonVariants({ variant: 'ghost' }),
                        'w-auto rounded-sm border-0 px-0 text-headline-semi hover:bg-muted',
                      )}
                    >
                      <p className="block">{selectedTunnelValue}</p>
                    </SelectTrigger>
                    <SelectContent>
                      {dashboardsList.map((dashboard) => (
                        <SelectItem key={dashboard.slug} value={dashboard.slug}>
                          {dashboard.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hasTable && (
                <div className="col-span-3">
                  <Button
                    className="h-full w-full justify-between p-4"
                    asChild={true}
                  >
                    <Link
                      to={`${params.slug}/alerts?startDate=${options.startDate}&endDate=${options.endDate}`}
                    >
                      <div className="flex flex-col">
                        <p className="text-body text-white">
                          {table.data?.summary[0].title}
                        </p>
                        <p className="text-4xl font-semibold text-amber-400">
                          <Icon
                            name="exclamation-triangle"
                            className="mr-2 h-6 w-6 fill-amber-400"
                          />
                          {table.data?.summary[0].value}
                        </p>
                      </div>
                      <Icon
                        name="arrow-top-right"
                        className="h-6 w-6 text-white"
                      />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-y-auto">
            <div className="grid h-full w-full grid-cols-1 gap-4 px-6">
              {charts
                .filter((chart) => chart.position !== -1)
                .map((chart) => (
                  <div
                    key={chart.id}
                    className="w-full rounded-2xl bg-secondary px-8 py-11"
                  >
                    {chart.type === 'synchronized-line-chart' && (
                      // @ts-expect-error - readonly should be optional
                      <SynchronizedLineChart
                        key={`${options.startDate}_${options.endDate}`}
                        id={chart.id}
                        theme={theme}
                        title={chart.title}
                        data={(chart.data ?? []) as SyncLineChartData}
                        schema={(chart.schema ?? []) as SyncLineChartSchema}
                        startDate={options.startDate}
                        endDate={options.endDate}
                        showExport={showExport}
                        onSelectExport={onSelectExport}
                      />
                    )}
                    {chart.type === 'heatmap' && (
                      // @ts-expect-error - readonly should be optional
                      <Heatmap
                        id={chart.id}
                        theme={theme}
                        data={chart.data}
                        startDate={options.startDate}
                        endDate={options.endDate}
                        title={chart.title}
                        schema={chart.schema as HeatmapSchema}
                        showExport={showExport}
                        onSelectExport={onSelectExport}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="relative h-full w-1/3">
          <ClientOnly>
            {() => (
              <Suspense fallback={null}>
                <MapClient
                  viewStateConfig={mapViewState}
                  // @ts-ignore
                  layersConfig={layers}
                />
              </Suspense>
            )}
          </ClientOnly>
        </div>
        <Outlet />
      </div>
    </main>
  );
}
